import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import {
  getDefaultContactForm,
  getDefaultCustomObjectForm,
  getDefaultThankYouPage,
} from '@kizen/page-builder/utils/build';
import { useTranslation } from 'react-i18next';
import KizenTypography from 'app/kizentypo';
import BasicModal from 'components/Modals/presets/BasicModal';
import TextInput from 'components/Inputs/TextInput';
import {
  createDefaultPage,
  createDefaultThankYouPage,
} from 'components/PageBuilder/utils';
import { isContactSurvey } from 'checks/survey';
import SurveyService from 'services/SurveyService';
import useFlashState from 'hooks/useFlashState';
import { getChosenBusiness } from 'store/authentication/selectors';
import { allowedRelationshipDropdownOptions } from 'xforms/api/customObjects';

import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import Dropdown from 'components/Fields/FieldInput/Dropdown';
import { FIELD_TYPES } from 'utility/constants';
import { getOriginalError } from '__services/AxiosService';

const ModalContents = styled.div`
  min-height: 220px; // Make room for open category selector
  margin-top: -5px;
`;

const StyledTextInput = styled(TextInput)`
  margin-bottom: 17px;
`;

const ErrorText = styled(KizenTypography)`
  color: var(--color-viz-cat-7-primary);
  margin-bottom: 10px;
  margin-top: -15px;
`;

export default function AddModal({
  models,
  show,
  onHide,
  onConfirm,
  ...props
}) {
  const { t } = useTranslation();
  const [surveyName, setSurveyName] = useState('');
  const [relatedObjectId, setRelatedObjectId] = useState(null);
  const [validateMessage, setValidateMessage] = useFlashState(1500, '');
  const [error, setError] = useState(false);
  const chosenBusiness = useSelector(getChosenBusiness);

  const modelOptions = useMemo(() => {
    return models.map(allowedRelationshipDropdownOptions);
  }, [models]);

  const handleSubmit = async () => {
    setValidateMessage('');
    setError(false);
    try {
      const newSurvey = await SurveyService.create({
        name: surveyName,
        relatedObjectId,
        templateType: 'modern', // 'modern' | 'splash' | 'open'
      });
      try {
        const businessData = {
          logo: chosenBusiness?.logo?.url
            ? {
                url: chosenBusiness?.logo?.url,
                name: chosenBusiness?.logo?.name,
                id: chosenBusiness?.logo?.id,
              }
            : null,
          name: chosenBusiness?.name,
          primaryColor: chosenBusiness?.primary_color,
        };
        const preview = await SurveyService.getPreview(newSurvey.id);
        const fieldNameLookup = preview.fields.reduce((acc, field) => {
          acc[field.name] = field;
          return acc;
        }, {});
        const thankYouPage = await getDefaultThankYouPage(
          'Thank you for submitting the survey.',
          businessData
        );
        const defaultSurvey = await (isContactSurvey(newSurvey)
          ? getDefaultContactForm(
              { ...fieldNameLookup.first_name, placeholder: t('Enter Text') },
              { ...fieldNameLookup.last_name, placeholder: t('Enter Text') },
              fieldNameLookup.email,
              businessData
            )
          : getDefaultCustomObjectForm(
              { ...fieldNameLookup.name, placeholder: t('Enter Text') },
              businessData
            ));
        const pages = [
          createDefaultPage(t('Survey Page'), defaultSurvey),
          createDefaultThankYouPage(t('Thank You Page'), thankYouPage),
        ];
        await SurveyService.update(newSurvey.id, { formUi: { pages } });
        onConfirm({ ...newSurvey, formUi: { pages } });
      } catch (err) {
        setError(true);
        await SurveyService.remove({ id: newSurvey.id });
      }
    } catch (err) {
      const error = getOriginalError(err);
      setValidateMessage(error?.name?.[0]);
    }
  };
  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners([
    { id: 'name' },
    {
      id: 'survey-object',
    },
  ]);

  return (
    <BasicModal
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      size="medium"
      heading={t('Add Survey')}
      onConfirm={handleSubmit}
      onHide={onHide}
      show={show}
      disabled={!surveyName || !relatedObjectId}
      {...props}
    >
      <KeyBoardContext.Provider value={{ assignFieldHandle }}>
        <ModalContents>
          {error && (
            <ErrorText>
              {t('There was an issue creating the survey. Please try again.')}
            </ErrorText>
          )}
          <StyledTextInput
            label={t('Survey Name')}
            placeholder={t('Enter Name')}
            value={surveyName}
            onChange={setSurveyName}
            inModal
            validate={
              validateMessage && {
                message: validateMessage,
                showMessage: true,
                inModal: true,
              }
            }
            {...getKeyListenersProps('name')}
          />
          <Dropdown
            label={t('Choose Survey Object')}
            placeholder={t('Choose Object')}
            options={modelOptions}
            value={relatedObjectId}
            onChange={(value) => setRelatedObjectId(value)}
            menuInline
            menuLeftButton={null}
            menuRightButton={null}
            field={{ fieldType: FIELD_TYPES.Dropdown, options: modelOptions }}
            {...getKeyListenersProps('survey-object')}
          />
        </ModalContents>
      </KeyBoardContext.Provider>
    </BasicModal>
  );
}
